/**
 * @module AdapterCard
 * @package @geenius/adapters/solidjs
 * @description Renders the Tailwind-based adapter summary card for the SolidJS
 * variant. The card surfaces provider status, latency, and domain metadata for
 * one adapter domain in the dashboard views.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
} from "@geenius/adapters";
import type { JSX } from "solid-js";
import { Show } from "solid-js";
import { cn } from "../lib/utils";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for rendering a single adapter summary card in the SolidJS dashboard grid.
 *
 * @property domain Adapter domain represented by the card.
 * @property status Current provider health and metadata for the domain.
 * @property onClick Optional callback fired when the card is activated.
 * @property onErrorAction Optional recovery callback shown when the status has an error.
 * @property errorActionLabel Optional label for the error recovery action.
 * @property class Optional class name merged into the card container.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  onErrorAction?: () => void;
  errorActionLabel?: string;
  class?: string;
}

/**
 * Renders a clickable SolidJS summary card for one adapter domain.
 *
 * @param props Card props including the adapter domain, status payload, and click handler.
 * @returns An interactive button when selection is enabled, otherwise a static summary card.
 */
export function AdapterCard(props: AdapterCardProps): JSX.Element {
  const content = (
    <>
      <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-border bg-muted text-2xl shadow-sm transition-transform duration-300 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100">
        {DOMAIN_ICONS[props.domain]}
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center justify-between gap-2">
          <h3 class="truncate text-sm font-bold text-foreground">
            {DOMAIN_LABELS[props.domain]}
          </h3>
          <AdapterStatusBadge status={props.status.status} />
        </div>
        <p class="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
          {DOMAIN_DESCRIPTIONS[props.domain]}
        </p>
        <Show when={props.status.provider && props.status.provider !== "none"}>
          <div class="mt-2 flex items-center gap-2">
            <span class="rounded-md border border-border bg-muted px-2 py-0.5 text-[0.625rem] font-semibold text-muted-foreground">
              {props.status.provider}
            </span>
            <Show when={props.status.latency}>
              <span class="text-[0.625rem] text-muted-foreground">
                {props.status.latency}ms
              </span>
            </Show>
          </div>
        </Show>
        <Show when={props.status.error}>
          {(error) => (
            <div class="mt-1.5 space-y-2">
              <p
                aria-live="polite"
                class="text-[0.625rem] text-destructive"
                role="alert"
              >
                {error()}
              </p>
              <Show when={props.onErrorAction}>
                <button
                  class="rounded-md border border-destructive/20 px-2 py-1 text-[0.625rem] font-semibold text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/20 motion-reduce:transition-none"
                  onClick={(event) => {
                    event.stopPropagation();
                    props.onErrorAction?.();
                  }}
                  type="button"
                >
                  {props.errorActionLabel ?? "Review adapter"}
                </button>
              </Show>
            </div>
          )}
        </Show>
      </div>
    </>
  );

  const className = cn(
    "group relative flex w-full items-start gap-4 overflow-hidden rounded-lg border border-border bg-card p-5 text-left text-card-foreground shadow-sm transition-colors duration-200 motion-reduce:transition-none",
    props.onClick
      ? "hover:border-primary/20 hover:bg-accent/5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      : "cursor-default",
    props.class,
  );

  if (!props.onClick || props.onErrorAction) {
    return (
      <article
        aria-label={`${DOMAIN_LABELS[props.domain]} adapter - ${props.status.status}`}
        class={className}
        onClick={props.onClick}
        onKeyDown={(event) => {
          if (!props.onClick || event.target !== event.currentTarget) {
            return;
          }

          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            props.onClick();
          }
        }}
        role={props.onClick ? "button" : undefined}
        tabIndex={props.onClick ? 0 : undefined}
      >
        {content}
      </article>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      class={className}
      aria-label={`${DOMAIN_LABELS[props.domain]} adapter - ${props.status.status}`}
    >
      {content}
    </button>
  );
}
